// V2 版式：bullet（前端 DOM 版）——对照后端 _shared/pptx/v2/layouts/bullet.ts 逐坐标移植
// 3–6 行 KPI 目标带；每行自有刻度：定性色带（差/合格/好，默认 60%/85% 分数位）衬底
// → accent 实测条 → 目标刻度竖线；max 缺省 = max(actual,target)×1.12 自动伸展。
// 后端对 target≤0 loud 拒渲染（lint BULLET_NO_TARGET 先行）；
// 前端预览对该行跳过容错（编辑器中间态不能崩页），正常数据下与后端几何一致。
// 几何取 V2GEO["bullet"]（deno dump 同源，勿手改数值）。

import { SPACING } from "../../lib/tokens.generated";
import { V2GEO } from "../../lib/v2geo.generated";
import { px, pt, titleSize, kickerOf, footOf } from "./shared";
import { shadeHex } from "./semantic";
import V2Chrome from "./Chrome";
import { EditableText } from "../SlideCanvas";
import type { V2LayoutProps } from "./V2Slide";

// deno dump 同源：与后端 bullet.ts GEO 一一对应
const GEO = V2GEO["bullet"] as any;

// bullet block 结构以后端 _shared/pptx/types.ts 为准（slides.ts Block 已同步）
type BulletItem = { label: string; actual: number; target: number; max?: number };
type BulletBlock = { type: "bullet"; unit?: string; items: BulletItem[] };

export default function V2Bullet({ c, skin, page, total, editable, handlers }: V2LayoutProps) {
  const m = SPACING.marginX;
  const W = 13.333;

  const bi = c.blocks.findIndex((b) => (b as { type: string }).type === "bullet");
  const bb = bi >= 0 ? (c.blocks[bi] as unknown as BulletBlock) : null;
  // 前端容错：target≤0 的行跳过（后端 loud 拒渲染；编辑中间态不崩页）
  const items = (bb ? bb.items : []).filter((it) => it.target > 0).slice(0, 6);
  const n = items.length;

  const plotH = GEO.chart.bottom - GEO.chart.top;
  const rowH = n > 0 ? (plotH - (n - 1) * GEO.rowGap) / n : plotH;
  const trackX = m + GEO.labelW + GEO.labelGap;
  const trackW = W - m - trackX - GEO.valW;

  // 定性色带（hairline 语义位深浅三档：差最浅 → 好最深）
  const bandBad = shadeHex(skin.hairline, 0.55);
  const bandPass = skin.hairline;
  const bandGood = shadeHex(skin.hairline, -0.3);

  return (
    <>
      <V2Chrome skin={skin} kicker={kickerOf(page, "bullet")} page={page} total={total} foot={footOf()} />

      {/* 页面标题（长度分档） */}
      <EditableText
        value={c.title} path="title" editable={editable} multiline
        onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
        style={{
          position: "absolute", left: px(GEO.title.x), top: px(GEO.title.y),
          width: px(GEO.title.w), height: px(GEO.title.h),
          fontSize: pt(titleSize(c.title, skin.scale.pageTitle)), color: skin.text,
          fontFamily: skin.fontHead, fontWeight: skin.serifHead ? 700 : 400, lineHeight: 1.15,
        }}
      />

      {items.map((it, i) => {
        const y = GEO.chart.top + i * (rowH + GEO.rowGap) + (rowH - GEO.bandH) / 2;
        // max 缺省 = max(actual,target)×1.12 自动伸展（与后端同公式）
        const max = it.max ?? Math.max(it.actual, it.target) * 1.12;
        const span = Math.max(max, 1e-9);
        const xOf = (v: number) => trackX + Math.max(0, Math.min(1, v / span)) * trackW;
        // 定性色带三段（差/合格/好，60%/85% 分数位）
        const segs: [number, number, string][] = [
          [0, GEO.passAt, bandBad],
          [GEO.passAt, GEO.goodAt, bandPass],
          [GEO.goodAt, 1, bandGood],
        ];
        const valX = trackX + trackW + 0.15;
        return (
          <div key={i}>
            {/* 行标签 */}
            <EditableText
              value={it.label} path={`blocks.${bi}.items.${i}.label`} editable={editable}
              onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
              style={{
                position: "absolute", left: px(m), top: px(y),
                width: px(GEO.labelW), height: px(GEO.bandH),
                fontSize: pt(skin.scale.body), color: skin.text, fontFamily: skin.fontBody,
                display: "flex", alignItems: "center", whiteSpace: "nowrap",
              }}
            />
            {/* 定性色带三档衬底 */}
            {segs.map(([a, b, color], j) => (
              <div key={j} style={{
                position: "absolute", left: px(xOf(a * span)), top: px(y),
                width: px(Math.max(xOf(b * span) - xOf(a * span), 0.01)), height: px(GEO.bandH),
                background: color,
              }} />
            ))}
            {/* accent 实测条（覆盖在色带上方，细条） */}
            <div style={{
              position: "absolute", left: px(trackX), top: px(y + GEO.bandH * 0.18),
              width: px(Math.max(xOf(it.actual) - trackX, 0.03)), height: px(GEO.bandH * 0.64),
              background: skin.accent,
            }} />
            {/* 目标刻度竖线（text 色，跨出轨道一小段） */}
            <div style={{
              position: "absolute", left: px(xOf(it.target)), top: px(y - 0.05),
              width: 1, height: px(GEO.bandH + 0.1), background: skin.text,
            }} />
            {/* 数值标签（actual / target，fontMono，右对齐） */}
            <EditableText
              value={it.actual.toLocaleString("en-US")}
              path={`blocks.${bi}.items.${i}.actual`} editable={editable}
              onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
              style={{
                position: "absolute", left: px(valX), top: px(y - 0.1),
                width: px(GEO.valW), height: px(GEO.bandH),
                fontSize: pt(skin.scale.meta), color: skin.accent, fontFamily: skin.fontMono,
                fontWeight: 700, textAlign: "right",
                display: "flex", alignItems: "center", justifyContent: "flex-end",
              }}
            />
            <EditableText
              value={`目标 ${it.target.toLocaleString("en-US")}${bb?.unit ?? ""}`}
              path={`blocks.${bi}.items.${i}.target`} editable={editable}
              onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
              style={{
                position: "absolute", left: px(valX), top: px(y + GEO.bandH * 0.45),
                width: px(GEO.valW), height: px(GEO.bandH * 0.8),
                fontSize: pt(skin.scale.meta), color: skin.sub, fontFamily: skin.fontMono,
                textAlign: "right", display: "flex", alignItems: "center", justifyContent: "flex-end",
              }}
            />
          </div>
        );
      })}
    </>
  );
}
